import {
  MessengerAbortError,
  type ConversationId,
  type Message,
  type MessengerSendTransport,
  type ProviderSendReceipt,
  type SendIntent,
  type SendMessageCommand,
} from '@crm/messenger';
import {
  asRecord,
  normalizeMessage,
  textValue,
  type JsonRecord,
} from './mello-messenger-mappers';

export class MessengerTransportUnavailableError extends Error {
  override readonly name = 'MessengerTransportUnavailableError';
  constructor() {
    super('The messaging provider transport is unavailable.');
  }
}

export interface SendRequestOptions {
  readonly method?: string;
  readonly body?: unknown;
  readonly signal?: AbortSignal;
}

export type SendRequest = (
  path: string,
  options: SendRequestOptions,
) => Promise<unknown>;

export interface McsSignalRSession {
  readonly hubUrl: string;
  readonly token: string;
}

export interface McsHubConnectionLike {
  readonly state?: string;
  invoke(method: string, payload: unknown): Promise<unknown>;
}

export interface McsHubConnector {
  connect(
    session: McsSignalRSession,
    refreshSession: () => Promise<McsSignalRSession>,
  ): Promise<McsHubConnectionLike>;
}

export class MelloMessengerSendTransport implements MessengerSendTransport {
  private hub: McsHubConnectionLike | null = null;

  constructor(
    private readonly deps: {
      readonly request: SendRequest;
      readonly connector?: McsHubConnector;
    },
  ) {}

  async validate(
    conversationId: ConversationId,
    command: SendMessageCommand,
    signal?: AbortSignal,
  ): Promise<SendIntent> {
    const response = asRecord(
      await this.deps.request(
        `/current-user/conversations/${encodeURIComponent(conversationId)}/messages/validate`,
        {
          method: 'POST',
          signal,
          body: {
            content: command.content,
            channelId: commandChannel(command),
            clientRequestId: command.clientMessageId,
            clientMessageId: command.clientMessageId,
            mediaAssetIds: command.mediaAssetIds?.length
              ? [...command.mediaAssetIds]
              : undefined,
            mediaUploads: command.mediaUploads?.length
              ? [...command.mediaUploads]
              : undefined,
            suggestionBatchId: command.suggestionBatchId,
            suggestionBatchVersion: command.suggestionBatchVersion,
            suggestionId: command.suggestionId,
            triggerMessageId: command.triggerMessageId,
            sendVCard: command.sendVCard === true,
            senderPersona: command.senderPersona,
          },
        },
      ),
    );
    return normalizeSendIntent(response, command);
  }

  async send(
    intent: SendIntent,
    signal?: AbortSignal,
  ): Promise<ProviderSendReceipt> {
    if (signal?.aborted) throw new MessengerAbortError();
    const connector = this.deps.connector;
    if (!connector) throw new MessengerTransportUnavailableError();
    const hub = await this.connect(connector);
    if (signal?.aborted) throw new MessengerAbortError();
    const result = asRecord(
      await hub.invoke('SendMessage', intent.mcsSendPayload ?? {}),
    );
    return {
      providerMessageId:
        textValue(result?.['providerMessageId']) ||
        textValue(result?.['ProviderMessageId']),
      conversationMessageGuid:
        textValue(result?.['conversationMessageGuid']) ||
        textValue(result?.['ConversationMessageGuid']) ||
        textValue(intent.mcsSendPayload?.['ConversationMessageGuid']),
    };
  }

  async commit(
    conversationId: ConversationId,
    intent: SendIntent,
    receipt: ProviderSendReceipt,
    signal?: AbortSignal,
  ): Promise<Message> {
    const response = asRecord(
      await this.deps.request(
        `/current-user/conversations/${encodeURIComponent(conversationId)}` +
          `/messages/${encodeURIComponent(intent.sendIntentId)}/committed`,
        {
          method: 'POST',
          signal,
          body: {
            providerMessageId: receipt.providerMessageId || undefined,
            conversationMessageGuid: receipt.conversationMessageGuid || undefined,
            providerStatus: 'sent',
          },
        },
      ),
    );
    const committed = asRecord(response?.['message']) ?? response ?? {};
    return {
      ...normalizeMessage(committed),
      id:
        textValue(committed['messageId']) ||
        receipt.conversationMessageGuid ||
        receipt.providerMessageId ||
        intent.sendIntentId,
      clientMessageId: intent.clientMessageId,
      sendIntentId: intent.sendIntentId,
      providerMessageId:
        receipt.providerMessageId || receipt.conversationMessageGuid || undefined,
      direction: 'outbound',
      status: textValue(committed['status']) || 'Sent',
    };
  }

  private async connect(
    connector: McsHubConnector,
  ): Promise<McsHubConnectionLike> {
    if (this.hub?.state === 'Connected') return this.hub;
    const session = await this.mcsSession();
    this.hub = await connector.connect(session, () => this.mcsSession());
    return this.hub;
  }

  private async mcsSession(): Promise<McsSignalRSession> {
    const record = asRecord(
      await this.deps.request('/current-user/mcs-signalr', {}),
    );
    return {
      hubUrl:
        textValue(record?.['hubUrl']) ||
        textValue(record?.['url']) ||
        textValue(record?.['endpoint']),
      token:
        textValue(record?.['token']) || textValue(record?.['accessToken']),
    };
  }
}

function commandChannel(command: SendMessageCommand): string {
  return (
    command.channelId ??
    (command.mediaAssetIds?.length || command.mediaUploads?.length
      ? 'mms'
      : 'sms')
  );
}

function normalizeSendIntent(
  response: JsonRecord | null,
  command: SendMessageCommand,
): SendIntent {
  const mcsSendPayload =
    asRecord(response?.['mcsSendPayload']) ??
    asRecord(response?.['mcsPayload']);
  const message = asRecord(response?.['message']);
  return {
    sendIntentId:
      textValue(response?.['sendIntentId']) || command.clientMessageId,
    clientMessageId:
      textValue(response?.['clientMessageId']) || command.clientMessageId,
    allowed: response?.['allowed'] === true,
    localOnly:
      response?.['localOnly'] === true ||
      (response?.['allowed'] === true && !mcsSendPayload),
    mcsSendPayload: mcsSendPayload ?? undefined,
    localMessage: message
      ? { ...normalizeMessage(message), direction: 'outbound' }
      : undefined,
  };
}
